import { ProjectStateFilterCounter } from "@/utils/ProjectStateFilterCounter";
import { ProjectStateFilterType } from "@/utils/ProjectStateFilter";
import {
  Task,
  ScheduledTask,
  TaskType,
  getEarliestTaskStartDate,
} from "@/data/models/Task";

import { DryingProjectOverview } from "../models/DryingProjectOverview";
import {
  AppointmentType,
  getLastestProjectState,
} from "@/data/models/ProjectState";
import { UserLookup } from "../models/UserLookup";
import {
  getAllOpenAppointments,
  isTimeOfAppointmentSet,
} from "@/data/models/DryingProjectOverview";

import {
  getRequieredQualificationsForTaskType,
  Qualification,
} from "@/data/models/Qualification";

import {
  getDefaultTaskDurationInMinutes,
  SchedulingSettings,
} from "@/data/models/SchedulingSettings";
import { DateTime } from "luxon";
import { PlanningParameters } from "@/data/models/PlanningParameters";

import {
  formatDateToHoursAndMinuteString,
  formatDateToYearMonthDateString,
} from "@/utils/DateHelper";

export function getTasks(
  runningDryingProjectOverviews: DryingProjectOverview[],
  userLookup: UserLookup,
  schedulingSettings: SchedulingSettings
) {
  const appointmentManagementFilters = [
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_LEAK_DETECTION_APPOINTMENT,
      [],
      true
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_DAMAGE_ASSESSMENT_APPOINTMENT,
      [],
      true
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_SETUP_DRYING_APPOINTMENT,
      [],
      true
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT,
      [],
      true
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_DRYING_CONCLUSION_APPOINTMENT,
      [],
      true
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_REPAIR_APPOINTMENT,
      [],
      true
    ),
  ];

  const today = new Date();
  const scheduledTasks: ScheduledTask[] = [];
  const tasksToSchedule: Task[] = [];

  runningDryingProjectOverviews.forEach((projectOverview) => {
    const step = getLastestProjectState(projectOverview, today);

    appointmentManagementFilters.forEach((filter) => {
      filter.visit(projectOverview, null, userLookup, today, step);
    });

    getAllOpenAppointments(projectOverview).filter((appointmentInfo) => {
      if (
        appointmentInfo.responsibleUser &&
        appointmentInfo.appointmentDate &&
        appointmentInfo.appointmentType
      ) {
        let taskType: TaskType | null = null;
        const requiredQualification: Qualification[] = [];

        switch (appointmentInfo.appointmentType) {
          case AppointmentType.DAMAGE_ASSESSMENT:
            taskType = TaskType.DAMAGE_ASSESSMENT;
            break;
          case AppointmentType.PREPARE_DRYING:
            taskType = TaskType.DRYING_SETUP;
            break;
          case AppointmentType.INTERMEDIATE_MEASUREMENT:
            taskType = TaskType.INTERMEDIATE_MEASUREMENT;
            break;
          case AppointmentType.DRYING_CONCLUSION:
            taskType = TaskType.DRYING_CONCLUSION;
            break;
          case AppointmentType.REPAIR:
            taskType = TaskType.REPAIR;
            requiredQualification.push(Qualification.REPAIR);
            break;
          case AppointmentType.FOLLOW_UP_INSPECTION:
            taskType = null;
            break;
          default:
            taskType = TaskType.LEAK_DETECTION;
            requiredQualification.push(Qualification.LEAK_DETECTION);
            break;
        }

        const userId = userLookup.getUserIdFromUserIdentifier(
          appointmentInfo.responsibleUser
        );

        if (taskType) {
          const durationInMin = getDefaultTaskDurationInMinutes(
            taskType,
            schedulingSettings
          );

          const earliestStartDate =
            getEarliestTaskStartDate(taskType, projectOverview) ?? new Date();

          const startDate = DateTime.fromISO(
            appointmentInfo.appointmentDate
          ).toJSDate();

          const yearMonthDateString = formatDateToYearMonthDateString(
            startDate
          );

          const planningParameters: PlanningParameters = {
            targetTime: null,
            isTargetTimeFixed: false,

            targetDate: yearMonthDateString,
            isTargetDateFixed: true,

            targetUserId: userId ? userId : null,
            isTargetUserFixed: !!userId,
          };

          const task: Task = {
            projectId: projectOverview.id,
            projectName: projectOverview.projectName ?? "",
            projectNumber: projectOverview.projectNumber ?? "",

            taskType: taskType,
            projectOwnerId: projectOverview.projectOwner ?? null,
            projectSupervisorId: projectOverview.projectSupervisorId ?? null,
            projectOwnerStaffNumber: projectOverview.projectOwnerStaffNumber ?? null,
            projectSupervisorStaffNumber: projectOverview.projectSupervisorStaffNumber ?? null,
            earliestStartDate: earliestStartDate,
            id: `${taskType}_${projectOverview.id}`,
            urgent: false,
            durationInMin: durationInMin,
            requiredQualification: getRequieredQualificationsForTaskType(
              taskType
            ),
            note: appointmentInfo.note,
            planningParameters: planningParameters,
          };

          if (isTimeOfAppointmentSet(startDate) && userId) {
            const endDate = new Date(
              startDate.getTime() + durationInMin * 60000
            );

            task.planningParameters.targetTime = formatDateToHoursAndMinuteString(
              startDate
            );
            task.planningParameters.isTargetTimeFixed = true;

            const scheduledTask: ScheduledTask = {
              userId: userId,
              wasConfirmed: true,
              startDate: startDate,
              endDate: endDate,
              ...task,
            };

            scheduledTasks.push(scheduledTask);
          } else {
            tasksToSchedule.push(task);
          }
        }
      }
    });
  });

  tasksToSchedule.push(
    ...appointmentManagementFilters.flatMap((filter) => {
      return filter.matchingProjectOverviews.map((projectOverviewWithState) => {
        const { projectOverview } = projectOverviewWithState;
        const requiredQualification: Qualification[] = [];

        let taskType: TaskType = TaskType.LEAK_DETECTION;

        switch (filter.filter.filterType) {
          case ProjectStateFilterType.MAKE_LEAK_DETECTION_APPOINTMENT: {
            taskType = TaskType.LEAK_DETECTION;
            requiredQualification.push(Qualification.LEAK_DETECTION);
            break;
          }
          case ProjectStateFilterType.MAKE_DAMAGE_ASSESSMENT_APPOINTMENT: {
            taskType = TaskType.DAMAGE_ASSESSMENT;
            requiredQualification.push(Qualification.BIG_DAMAGE_ASSESSMENT);
            break;
          }
          case ProjectStateFilterType.MAKE_SETUP_DRYING_APPOINTMENT: {
            taskType = TaskType.DRYING_SETUP;
            requiredQualification.push(Qualification.DRYING);
            break;
          }
          case ProjectStateFilterType.MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT: {
            taskType = TaskType.INTERMEDIATE_MEASUREMENT;
            requiredQualification.push(Qualification.DRYING);
            break;
          }
          case ProjectStateFilterType.MAKE_DRYING_CONCLUSION_APPOINTMENT: {
            taskType = TaskType.DRYING_CONCLUSION;
            requiredQualification.push(Qualification.DRYING);
            break;
          }
          case ProjectStateFilterType.MAKE_REPAIR_APPOINTMENT: {
            taskType = TaskType.REPAIR;
            requiredQualification.push(Qualification.REPAIR);
            break;
          }
        }

        const earliestStartDate =
          getEarliestTaskStartDate(taskType, projectOverview) ?? new Date();

        const planningParameters: PlanningParameters = {
          targetDate: null,
          targetTime: null,
          targetUserId: null,
          isTargetDateFixed: false,
          isTargetTimeFixed: false,
          isTargetUserFixed: false,
        };

        return {
          projectId: projectOverview.id,
          projectName: projectOverview.projectName ?? "",
          projectNumber: projectOverview.projectNumber ?? "",

          urgent: false,
          earliestStartDate: earliestStartDate,
          projectOwnerId: projectOverview.projectOwner ?? null,
          projectSupervisorId: projectOverview.projectSupervisorId ?? null,
          projectOwnerStaffNumber: projectOverview.projectOwnerStaffNumber ?? null,
          projectSupervisorStaffNumber: projectOverview.projectSupervisorStaffNumber ?? null,
          taskType,
          id: `${taskType}_${projectOverview.id}`,
          durationInMin: getDefaultTaskDurationInMinutes(
            taskType,
            schedulingSettings
          ),
          requiredQualification: getRequieredQualificationsForTaskType(
            taskType
          ),
          planningParameters,
        };
      });
    })
  );

  return {
    scheduledTasks,
    tasksToSchedule,
  };
}
